import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { locationsAPI, usersAPI, currenciesAPI } from '../../services/api';
import { Location, User, UserRole, CreateLocationDto, UpdateLocationDto, Currency } from '../../types';
import { useTranslation } from 'react-i18next';
import { useCompany } from '../CompanyContext';
import {
  BuildingOfficeIcon,
  ArrowLeftIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const LocationForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedCompany } = useCompany();
  const isEdit = Boolean(id);

  // Form data interface that ensures required fields are present
  interface LocationFormData {
    name: string;
    address: string;
    phone: string;
    email: string;
    managerId: string;
    currencyId: string;
    isActive: boolean;
  }

  const [formData, setFormData] = useState<LocationFormData>({
    name: '',
    address: '',
    phone: '',
    email: '',
    managerId: '',
    currencyId: '',
    isActive: true,
  });
  const [managers, setManagers] = useState<User[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  useEffect(() => {
    if (selectedCompany) {
    fetchManagers();
    fetchCurrencies();
      if (isEdit && id) {
        fetchLocation();
      }
    }
  }, [id, isEdit, selectedCompany]);

  const fetchLocation = async () => {
    if (!id) return;
    
    try {
      setInitialLoading(true);
      const data = await locationsAPI.getById(id);
      setFormData({
        name: data.name,
        address: data.address || '',
        phone: data.phone || '',
        email: data.email || '',
        managerId: data.managerId || '',
        currencyId: data.currencyId || '',
        isActive: data.isActive,
      });
    } catch (err: any) {
      console.error('Error fetching location:', err);
      setError(err.response?.data?.message || 'Failed to fetch location');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const allUsers = await usersAPI.getByCompany(selectedCompany?.id!);
      const branchManagers = allUsers.filter(user => user.role === UserRole.BRANCH_MANAGER);
      setManagers(branchManagers);
    } catch (err: any) {
      console.error('Error fetching managers:', err);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const allCurrencies = await currenciesAPI.getAll();
      const activeCurrencies = allCurrencies.filter(currency => currency.isActive);
      setCurrencies(activeCurrencies);
    } catch (err: any) {
      console.error('Error fetching currencies:', err);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Location name is required');
      return;
    }

    if (!formData.currencyId) {
      setError('Currency is required');
      return;
    }

    if (!selectedCompany) {
      setError('Please select a company first');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isEdit && id) {
        const updateData: UpdateLocationDto = {
          name: formData.name.trim(),
          companyId: selectedCompany.id,
          currencyId: formData.currencyId,
          managerId: formData.managerId || undefined,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
          isActive: formData.isActive,
        };
        await locationsAPI.update(id, updateData);
      } else {
        const createData: CreateLocationDto = {
          name: formData.name.trim(),
          companyId: selectedCompany.id,
          currencyId: formData.currencyId,
          managerId: formData.managerId || undefined,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
          email: formData.email || undefined,
          isActive: formData.isActive,
        };
        await locationsAPI.create(createData);
      }

      navigate('/locations');
    } catch (err: any) {
      console.error('Error saving location:', err);
      setError(err.response?.data?.message || 'Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/locations"
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-primary-600 mr-3" />
            {isEdit ? t('locations.editLocation') : t('locations.addLocation')}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEdit 
              ? t('locations.editLocationDescription') 
              : t('locations.addLocationDescription')
            }
          </p>
        </div>
      </div>

      {/* Company Info */}
      {selectedCompany && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-5 w-5 text-primary-600 mr-2" />
            <span className="text-sm font-medium text-primary-900">
              {t('locations.selectedCompany')}: {selectedCompany.name}
            </span>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Location Name */}
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('locations.fields.name')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('locations.placeholders.name')}
                />
              </div>
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                {t('locations.fields.address')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPinIcon className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('locations.placeholders.address')}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                {t('locations.fields.phone')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('locations.placeholders.phone')}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('locations.fields.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('locations.placeholders.email')}
                />
              </div>
            </div>

            {/* Manager */}
            <div>
              <label htmlFor="managerId" className="block text-sm font-medium text-gray-700 mb-1">
                {t('locations.fields.manager')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="managerId"
                  name="managerId"
                  value={formData.managerId}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                >
                  <option value="">{t('locations.placeholders.selectManager')}</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.firstName} {manager.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('locations.hints.managerNote')}
              </p>
            </div>

            {/* Currency */}
            <div>
              <label htmlFor="currencyId" className="block text-sm font-medium text-gray-700 mb-1">
                Currency <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-sm">$</span>
                </div>
                <select
                  id="currencyId"
                  name="currencyId"
                  value={formData.currencyId}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                >
                  <option value="">Select Currency</option>
                  {currencies.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Currency used for orders and sales at this location
              </p>
            </div>

            {/* Active Status */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">{t('locations.fields.isActive')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                {t('locations.hints.activeNote')}
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Link
              to="/locations"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('locations.actions.cancel')}
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('locations.actions.saving')}
                </div>
              ) : (
                isEdit ? t('locations.actions.update') : t('locations.actions.create')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationForm;
